/**
 * DisqualifyModal — capture WHY a lead doesn't qualify (the structured DQ
 * ledger write). Criterion is preselected when the runner caught the failure;
 * the category auto-follows the chosen criterion but stays editable. Step 2
 * (Phase F) suggests the other recruiting studies the contact may fit —
 * "Start prescreen" pivots straight into a new runner via onPivot, Done takes
 * the classic onRecorded exit. Once the DQ is saved every exit (backdrop
 * included) goes through onRecorded so the parent refetches.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { PrescreenCriterion, studyInternalLabel } from '../../types';
import { DQ_CATEGORIES, notifyStatsChanged } from '../../lib';
import PivotPanel from './PivotPanel';
import { Z } from '../ui';

interface DisqualifyModalProps {
  contactId: string;
  study: { id: string; title: string };
  attemptId?: string | null;
  criteria?: PrescreenCriterion[];
  preselectedCriterion?: PrescreenCriterion | null;
  /** Pre-fills the Details field — the actual failing answer(s) so the recorded
   *  reason captures the disqualifying VALUE, not just the question. */
  prefillDetails?: string;
  onClose: () => void;
  onRecorded: () => void;
  /** Phase F: a pivot prescreen was started — open the runner on this study. */
  onPivot?: (study: { id: string; title: string }) => void;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

export default function DisqualifyModal({
  contactId, study, attemptId = null, criteria = [], preselectedCriterion = null, prefillDetails = '', onClose, onRecorded, onPivot,
}: DisqualifyModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [criterionId, setCriterionId] = useState<string>(preselectedCriterion?.id || '');
  const [category, setCategory] = useState<string>(preselectedCriterion?.category || 'other');
  // Seed Details with the actual failing answer(s) so the disqualifying VALUE is
  // recorded (the recruiter can still edit before saving).
  const [note, setNote] = useState(prefillDetails);
  const [saving, setSaving] = useState(false);
  const [pivotBusy, setPivotBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // The step-1 submit button unmounts on the flip to step 2 — without this,
  // focus falls to <body> behind the still-open dialog.
  useEffect(() => {
    if (step === 2) dialogRef.current?.focus();
  }, [step]);

  // Category follows the selected criterion (still hand-editable afterwards).
  useEffect(() => {
    const c = criteria.find((x) => x.id === criterionId);
    if (c) setCategory(c.category);
  }, [criterionId, criteria]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/crm/contacts/${contactId}/disqualifications`, {
        study_id: study.id,
        attempt_id: attemptId || undefined,
        criterion_id: criterionId || undefined,
        category,
        note: note.trim() || undefined,
      });
      // The DQ is saved — pivot suggestions next; onRecorded waits for Done.
      notifyStatsChanged();
      setSaving(false);
      setStep(2);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to record disqualification');
      setSaving(false);
    }
  };

  // Pre-save a dismissal is a plain close; post-save the parent must refetch.
  // While EITHER POST (the DQ save or a step-2 rescreen) is in flight NO exit
  // is allowed — an unmount mid-save would orphan the recorded DQ, and a
  // dismissal mid-rescreen would resurrect the runner when the POST resolves.
  const busy = saving || pivotBusy;
  const dismiss = step === 2 ? onRecorded : onClose;

  return (
    <div className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`} onClick={busy ? undefined : dismiss}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-white p-5 shadow-xl focus:outline-none dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={step === 1 ? 'Record disqualification' : 'Other studies they may fit'}
      >
        {step === 1 ? (
          <>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Did not qualify — {studyInternalLabel(study)}</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              The reason is recorded permanently and powers the screening reports.
            </p>

            <div className="mt-4 space-y-3">
              {criteria.length > 0 && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Failed criterion</label>
                  <select value={criterionId} onChange={(e) => setCriterionId(e.target.value)} className={inputCls}>
                    <option value="">(not tied to a specific criterion)</option>
                    {criteria.map((c) => (
                      <option key={c.id} value={c.id}>
                        [{c.criterion_kind === 'exclusion' ? 'EXCL' : 'INCL'}] {c.display_question.slice(0, 80)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                  {DQ_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Details (optional)</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                  placeholder="e.g. A1c 11.2, above the 10.5 cap"
                  className={inputCls}
                />
              </div>
            </div>

            {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-red-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 disabled:opacity-50 sm:min-h-0"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Recording…' : 'Record disqualification'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Phase F step 2: cross-study pivot suggestions. */}
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Recorded. They may fit these studies instead:
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">✓ likely fits · ? unknown · ✗ doesn't fit</p>

            <div className="mt-3 max-h-[60vh] overflow-y-auto">
              <PivotPanel
                contactId={contactId}
                fromStudyId={study.id}
                onStart={(s) => { setPivotBusy(false); if (onPivot) onPivot(s); else onRecorded(); }}
                onBusyChange={setPivotBusy}
              />
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onRecorded}
                disabled={pivotBusy}
                className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
